<script lang="ts" setup>
import { watch } from "vue";
import type { TripMonitorItem, TripMonitorRoute, TripMonitorTravelOrder } from "@/api/trips/dto/trip-monitor";
import type { TripUpdate } from "@/api/trips/dto/update";
import CreateBrokerUpdateForm from "@/views/trips/components/forms/CreateBrokerUpdateForm.vue";
import ViewUpdateDialog from "@/views/trips/components/dialogs/ViewUpdateDialog.vue";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import { usePipStore } from "@/store/pip.store";
import TripIntervalDialog from "@/views/trips/components/TripIntervalDialog.vue";
import type { BrokerUpdateFormProps } from "@/types/props";

const api = new API();
const { showSnackbarError, showSnackbarSuccess } = useSnackbarStore();
const pipStore = usePipStore();

const showCreateDialog = defineModel<boolean>("showCreateDialog", { default: false });
const showInfoDialog = defineModel<boolean>("showInfoDialog", { default: false });
const showIntervalDialog = defineModel<boolean>("showIntervalDialog", { default: false });

const props = withDefaults(
  defineProps<{
    tripMonitorRoute?: TripMonitorRoute | null;
    travelOrder?: TripMonitorTravelOrder | null;
    brokerUpdate?: TripUpdate | null;
    trip?: TripMonitorItem | null;
  }>(),
  {
    tripMonitorRoute: null,
    brokerUpdate: null,
    trip: null,
    travelOrder: null,
  },
);

async function openBrokerUpdatePip(): Promise<void> {
  const lastCheckCall = props.tripMonitorRoute?.lastCheckCall;

  if (!props.tripMonitorRoute || !lastCheckCall || !props.travelOrder?.id) {
    showCreateDialog.value = false;

    return;
  }

  const brokerUpdateFormProps: BrokerUpdateFormProps = {
    checkCallId: lastCheckCall.id,
    checkCallType: lastCheckCall.type,
    routeId: props.tripMonitorRoute.id,
    travelOrderId: props.travelOrder.id,
    orders: props.trip?.orders ?? [],
  };

  await pipStore.open({
    component: CreateBrokerUpdateForm,
    props: brokerUpdateFormProps,
    on: {
      close: () => pipStore.close(),
    },
  });

  showCreateDialog.value = false;
}

async function updateInterval(interval: number | null): Promise<void> {
  if (!props.travelOrder || !props.trip) {
    return;
  }

  try {
    const response = await api.trips.updateTravelOrderInterval(props.trip.id, props.travelOrder.id, interval);

    if (response.success) {
      showSnackbarSuccess("Interval saved successfully");
    }
  } catch (error) {
    console.log(error);
    showSnackbarError("Could not save the trip interval");
  }
}

watch(showCreateDialog, (show) => {
  if (!show) {
    return;
  }

  openBrokerUpdatePip();
});
</script>

<template>
  <view-update-dialog
    v-if="brokerUpdate && props.tripMonitorRoute?.lastCheckCall && showInfoDialog"
    v-model="showInfoDialog"
    :check-call-type="props.tripMonitorRoute.lastCheckCall.type"
    :update="brokerUpdate"
  />

  <trip-interval-dialog
    v-if="props.tripMonitorRoute && props.travelOrder && showIntervalDialog"
    v-model="showIntervalDialog"
    :interval="props.travelOrder.updateInterval"
    :truck-id="props.tripMonitorRoute.truck.id"
    :truck-number="props.tripMonitorRoute.truck.number"
    title="Broker update settings"
    @update-interval="updateInterval"
  />
</template>

<style lang="scss" scoped></style>
